import React, { useEffect, useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { CheckCircleIcon, Flower2Icon, LeafIcon, SparklesIcon } from 'lucide-react';
const Results: React.FC = () => {
  const {
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState({
    primaryGarden: '',
    secondaryGarden: '',
    healingPath: '',
    recommendedFeatures: [] as string[]
  });
  useEffect(() => {
    // Calculate completion time when results are shown
    if (assessmentData.startTime) {
      const completionTime = (Date.now() - assessmentData.startTime) / 1000; // in seconds
      updateAssessmentData({
        completionTime,
        timestamp: new Date()
      });
    }
    // Simulate API call to process assessment data
    const timer = setTimeout(() => {
      // Determine results based on assessment data
      // This is a simplified example - in a real app, this would be more sophisticated
      const primaryHealing = assessmentData.primaryHealing || 'phoenix';
      // Map healing intent to primary garden
      const gardenMap: Record<string, string> = {
        phoenix: 'Bravery',
        dove: 'Love',
        wolf: 'Respect',
        butterfly: 'Humility',
        eagle: 'Wisdom',
        owl: 'Truth',
        dragon: 'Honesty'
      };
      // Generate results
      setResults({
        primaryGarden: gardenMap[primaryHealing] || 'Wisdom',
        secondaryGarden: 'Love',
        healingPath: primaryHealing === 'phoenix' ? 'Resilience Builder' : primaryHealing === 'dove' ? 'Self-Compassion Journey' : primaryHealing === 'wolf' ? 'Authentic Expression Path' : primaryHealing === 'butterfly' ? 'Emotional Balance Way' : primaryHealing === 'eagle' ? 'Purpose Finder' : primaryHealing === 'owl' ? 'Intuitive Wisdom Path' : 'Spiritual Integration Journey',
        recommendedFeatures: ['Daily Reflection Prompts', 'Guided Meditations', 'Garden Restoration Quests', 'Community Wisdom Circles']
      });
      setIsLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [assessmentData, updateAssessmentData]);
  const handleContinue = () => {
    setCurrentStep(8); // Move to CommunityOptIn
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-690">
      <AnimatedBackground type="cosmic" data-id="element-691" />
      <ParticleEffect count={70} data-id="element-692" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-693">
        <LeafIcon size={80} className="text-green-200" data-id="element-694" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-695">
        <LeafIcon size={100} className="text-green-200" data-id="element-696" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-697">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-698" />
      </div>
      {/* Luminara image - only shown when results are loaded */}
      {!isLoading && <div className="absolute top-1/3 right-5 opacity-30 z-0 hidden md:block" data-id="element-699">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qZVheqmct1MU2ySN36VNGU/openart-6db1d22a821d449cab14977aa32af312_raw.png" alt="Luminara ethereal guardian" className="w-60 h-auto" data-id="element-700" />
        </div>}
      <div className="w-full max-w-4xl" data-id="element-701">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-6" data-id="element-702">
          <span className="text-pink-300" data-id="element-703">Seven</span>After
        </h1>
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-704">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-705"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-706"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-707">
            Your Sacred Path Results
          </h2>
          {isLoading ? <div className="py-20 flex flex-col items-center justify-center relative z-10" data-id="element-708">
              <div className="w-16 h-16 border-4 border-pink-300 border-t-transparent rounded-full animate-spin mb-4" data-id="element-709"></div>
              <p className="text-white text-center" data-id="element-710">
                Creating your personalized sacred garden journey...
              </p>
            </div> : <div className="relative z-10" data-id="element-711">
              <div className="flex justify-center mb-6" data-id="element-712">
                <div className="bg-gradient-to-br from-pink-500/30 to-purple-500/30 p-4 rounded-full relative" data-id="element-713">
                  <SparklesIcon size={48} className="text-white" data-id="element-714" />
                  <div className="absolute top-0 left-0 w-full h-full rounded-full animate-pulse bg-white opacity-20" data-id="element-715"></div>
                </div>
              </div>
              <p className="text-white text-center mb-6" data-id="element-716">
                Based on your responses, we've crafted a unique healing journey
                through our sacred gardens.
              </p>
              <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-6" data-id="element-717">
                <h3 className="text-xl font-bold text-white mb-4 text-center" data-id="element-718">
                  Your Personalized Path
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-719">
                  <div className="bg-white bg-opacity-20 rounded-lg p-4" data-id="element-720">
                    <h4 className="font-medium text-white mb-1" data-id="element-721">
                      Primary Garden
                    </h4>
                    <p className="text-pink-200 text-lg" data-id="element-722">
                      {results.primaryGarden}
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-4" data-id="element-723">
                    <h4 className="font-medium text-white mb-1" data-id="element-724">
                      Secondary Garden
                    </h4>
                    <p className="text-pink-200 text-lg" data-id="element-725">
                      {results.secondaryGarden}
                    </p>
                  </div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4" data-id="element-726">
                  <h4 className="font-medium text-white mb-1" data-id="element-727">
                    Your Healing Path
                  </h4>
                  <p className="text-pink-200 text-lg" data-id="element-728">{results.healingPath}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4" data-id="element-729">
                  <h4 className="font-medium text-white mb-2" data-id="element-730">
                    Recommended Features
                  </h4>
                  <ul className="space-y-2" data-id="element-731">
                    {results.recommendedFeatures.map((feature, index) => <li key={index} className="flex items-center" data-id="element-732">
                        <CheckCircleIcon size={16} className="text-green-300 mr-2" data-id="element-733" />
                        <span className="text-white" data-id="element-734">{feature}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="text-center text-white mb-6" data-id="element-735">
                <p className="italic text-sm" data-id="element-736">
                  "Your journey through the seven gardens is both unique and
                  universal. Trust the path that calls to you." - Luminara,
                  Ethereal Guardian
                </p>
              </div>
              <button onClick={handleContinue} className="w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer mystical-button glow" data-id="element-737">
                Continue to Create Your Account
              </button>
            </div>}
        </div>
      </div>
    </div>;
};
export default Results;